// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
//
// The semantic palette, in one place, so colour cannot drift between views.
//
// There are four meanings and no fifth. The whole argument of this site is a
// comparison between two quantities, so those two quantities own two hues
// outright and nothing else may borrow them.
//
// A deliberate property for colour-blind readers: OCHRE never appears below
// zero and CRIMSON never appears above it, so hue is always redundant with
// position on every signed chart. A deuteranopic reader who cannot separate
// teal from ochre can still read every chart from geometry alone.

/** What the FARMING earned. */
export const FARMING = '#14706B';
/** What the LAND appreciated. Also debt, drawn dashed — the asset's shadow. */
export const LAND = '#B8791F';
/** Below zero. Never "debt", never "a bad region" — only "under the zero rule". */
export const CRIMSON = '#A32C1E';
/** Money leaving, and neutral structure: costs, depreciation, reference lines. */
export const GRAPHITE = '#3E4247';
/** No reliable estimate. Never used decoratively. */
export const NEUTRAL = '#8A8A82';

export const PAPER = '#F2EFE6';

/** Zone tints — desaturated, because a zone is a GROUPING, never a measure. */
export const ZONE_COLOUR: Record<string, string> = {
  Pastoral: '#B08A5D',
  'Wheat Sheep': '#CBB26A',
  'High Rainfall': '#7FA593',
};

export const ZONE_ORDER = ['High Rainfall', 'Wheat Sheep', 'Pastoral'];

/**
 * The diverging ramp for a rate of return, ANCHORED AT ZERO — never at the data
 * midpoint, and never quantile or Jenks classed.
 *
 * Zero is a real, meaningful anchor here: it is the line between farming that
 * pays for its own capital and farming that does not. A quantile scale would
 * put that line wherever the data happened to fall and would paint a region
 * earning +0.02% the same colour as one earning +3%.
 *
 * `positiveHue` switches the top end between teal (counting the farming only)
 * and ochre (counting the land too), on an identical domain — so flipping the
 * map changes which engine is counted, not the scale.
 */
export function returnColour(v: number | null, max: number, positiveHue: string = FARMING): string {
  if (v == null || !Number.isFinite(v)) return NEUTRAL;
  const m = Math.max(max, 0.5);
  const t = Math.max(-1, Math.min(1, v / m));
  if (t >= 0) return mixHex(PAPER, positiveHue, t);
  return mixHex(PAPER, CRIMSON, -t);
}

export function mixHex(a: string, b: string, k: number): string {
  const pa = parse(a); const pb = parse(b);
  if (!pa || !pb) return a;
  const c = (i: 0 | 1 | 2) => Math.round(pa[i] + (pb[i] - pa[i]) * Math.max(0, Math.min(1, k)));
  return `#${[c(0), c(1), c(2)].map((n) => n.toString(16).padStart(2, '0')).join('')}`;
}

function parse(h: string): [number, number, number] | null {
  const m = /^#?([0-9a-f]{6})$/i.exec(h.trim());
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

/**
 * The scope sentence.
 *
 * Appears on every view, in these words, every time. The site never says
 * "Australian farming" — this survey does not cover horticulture, sugar,
 * cotton as an industry, poultry, pigs or vegetables, and a reader who takes
 * one of these charts for all of Australian agriculture has been misled by us,
 * not by ABARES.
 */
export const SCOPE = 'ABARES broadacre farm survey (AAGIS), 32 regions, 1990–2025, plus dairy (ADIS) at national and state level only. Horticulture, sugar, cotton as an industry, poultry, pigs and vegetables are not surveyed. All figures are per surveyed farm, in nominal dollars, and are survey estimates with sampling error — not a census.';

export const SCOPE_SHORT = 'ABARES broadacre survey · per surveyed farm · nominal dollars · sample estimates, not a census';
